import { ANNOTATION_DIVIDENDS, ANNOTATION_EARNINGS_RESULTS, ANNOTATION_LINE_LABEL_INFO, ANNOTATION_SPINOFFS, ANNOTATION_SPLITS, CHART_ARROW_HEIGHT, CHART_ARROW_PATH, CHART_FORMAT_CURRENCY_GROWTH, CHART_FORMAT_NORMALIZED, CHART_FORMAT_PERCENT_OFF_HIGH, CHART_FORMAT_REAL, LINE_WEIGHT_MAPPING, MIN_CHART_ARROW_WIDTH, TOOLTIP_STYLE_BAR, TOOLTIP_STYLE_BOX, } from 'ChartApp/ts/features/fundamental-chart/constants';
import { ValueFlagOption } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { AnnualizedReturnsService } from 'Angular/utils/annualized-returns';
import dateFormatter from 'Angular/utils/date_formatter';
import { getBasicFormatted, getFormatter } from 'Angular/utils/number_formatter';
import { BaseYchartFund } from 'ChartApp/ts/features/fundamental-chart/base_ychart_fund';
import Highcharts from 'ChartApp/ts/features/fundamental-chart/highcharts';
export default class YchartFundInteractive extends BaseYchartFund {
    // The caller passes a partial config (the fund-chart page's configurable options); `setDefaults()`
    // fills in the rest. Typed loosely here to preserve that contract, matching the prior JS interface.
    constructor(highchartOptions, config) {
        super(highchartOptions, config);
        this.annualizedReturnsService = AnnualizedReturnsService;
        this.dateFormatter = dateFormatter;
        this.createTooltips = () => {
            const ychartFund = this;
            const { chartData, config } = this;
            return function (xVal) {
                let numItems = 0;
                // For each series, find the closest point at or before the x-value (timestamp)
                const closestXMap = {};
                let dataSeries = [];
                for (let i = 0; i < chartData.length; i += 1) {
                    closestXMap[i] = {};
                    for (let j = 0; j < chartData[i].length; j += 1) {
                        dataSeries = chartData[i][j].raw_data;
                        for (let k = 0; k < dataSeries.length; k += 1) {
                            // look for the first data point that's greater than or
                            // equal to our x position and go one back
                            if (dataSeries[k][0] >= xVal) {
                                // If we find a point greater than our x position,
                                // then we find the first point going backwards that's non-NULL
                                let m;
                                if (dataSeries[k][0] === xVal) {
                                    m = k;
                                }
                                else {
                                    m = k - 1;
                                }
                                for (m; m >= 0; m -= 1) {
                                    if (dataSeries[m][1] !== null) {
                                        break;
                                    }
                                }
                                // If we were able to find a valid x position, save it!
                                if (m >= 0) {
                                    closestXMap[i][j] = m;
                                    numItems += 1;
                                }
                                break;
                            }
                            // If we're at the end of the series, save the last point!
                            if (k + 1 === dataSeries.length && dataSeries[k][1] !== null) {
                                closestXMap[i][j] = k;
                                numItems += 1;
                                break;
                            }
                        }
                    }
                }
                // If there are no items to put in the tooltip, clear the current tooltip and stop
                if (numItems === 0) {
                    return ychartFund.resetDataTooltip();
                }
                const tooltipData = [];
                for (let i = 0; i < chartData.length; i += 1) {
                    tooltipData.push([]);
                    // Start date / start val for annualized returns
                    chartData[i].forEach((chartDataSeries, seriesIdx) => {
                        const { color } = chartDataSeries.line_format;
                        // Do not try to set up any tooltips if we have no data.
                        if (chartDataSeries.raw_data.length === 0)
                            return;
                        // If the data series does not have a point to display in the tooltip, skip it
                        // just store the label and move on !!!
                        if (closestXMap[i][seriesIdx] === undefined) {
                            tooltipData[i].push({
                                color,
                                longLabel: chartDataSeries.long_label,
                                shortLabel: chartDataSeries.short_label,
                                val1: '',
                                val2: '',
                            });
                            return;
                        }
                        const xIdx = closestXMap[i][seriesIdx];
                        // Find the datapoint that matches the underlying data
                        const { quarterlyFormat } = ychartFund.config;
                        const xFormatter = ychartFund.dateFormatter.getFormatter(chartDataSeries.frequency, quarterlyFormat);
                        const xFormatted = xFormatter ? xFormatter(chartDataSeries.raw_data[xIdx][0]) : '';
                        const { format } = chartDataSeries;
                        const currencyCode = BaseYchartFund.getChartSeriesCurrencyCode(format, chartDataSeries.currency_code);
                        const yFormatter = getFormatter(chartDataSeries.type, currencyCode, ychartFund.locale);
                        let yFormatted;
                        if (ychartFund.config.yValueTooltipFormatter != null) {
                            yFormatted = ychartFund.config.yValueTooltipFormatter(chartDataSeries.raw_data[xIdx][1]);
                        }
                        else {
                            yFormatted = yFormatter(chartDataSeries.raw_data[xIdx][1]) ?? '';
                        }
                        // Set to empty string for template check if absent
                        let annualizedValue;
                        if (config.annualizedReturns &&
                            chartDataSeries.can_annualize &&
                            format !== CHART_FORMAT_PERCENT_OFF_HIGH) {
                            const startDate = chartData[i][seriesIdx].raw_data[0][0];
                            const startVal = chartData[i][seriesIdx].raw_data[0][1];
                            switch (format) {
                                case CHART_FORMAT_CURRENCY_GROWTH:
                                case CHART_FORMAT_REAL:
                                    annualizedValue = ychartFund.annualizedReturnsService.annualizeReturns(startDate, startVal, chartDataSeries.raw_data[xIdx][0], chartDataSeries.raw_data[xIdx][1]);
                                    break;
                                case CHART_FORMAT_NORMALIZED:
                                    annualizedValue = ychartFund.annualizedReturnsService.annualizeNormalizedReturns(startDate, chartDataSeries.raw_data[xIdx][1], chartDataSeries.raw_data[xIdx][0]);
                                    break;
                                default:
                                    break;
                            }
                            /* eslint-disable no-restricted-globals */
                            if (isNaN(annualizedValue))
                                annualizedValue = '--';
                        }
                        // For area range line charts display both the top and bottom values.
                        if (chartDataSeries.line_format.arearange_chart) {
                            const bottomValueFormatted = yFormatter(chartDataSeries.raw_data[xIdx][1]) ?? '';
                            const topValueFormatted = yFormatter(chartDataSeries.raw_data[xIdx][2]) ?? '';
                            // We're only honoring tooltip_include_timestamps in area range for scenarios future dates, we
                            // otherwise do not need this option, and in fact have other logic to "patch" missing tooltip
                            // data. Will later move this option outside this context if necessary
                            if (chartDataSeries.tooltip_include_timestamps == null ||
                                chartDataSeries.tooltip_include_timestamps.indexOf(xVal) > -1) {
                                tooltipData[i].push({
                                    annualizedValue,
                                    color,
                                    longLabel: chartDataSeries.long_label,
                                    shortLabel: chartDataSeries.high_short_label,
                                    val1: xFormatted,
                                    val2: topValueFormatted,
                                }, {
                                    annualizedValue,
                                    color,
                                    longLabel: chartDataSeries.long_label,
                                    shortLabel: chartDataSeries.low_short_label,
                                    val1: xFormatted,
                                    val2: bottomValueFormatted,
                                });
                            }
                        }
                        else {
                            tooltipData[i].push({
                                annualizedValue,
                                color,
                                longLabel: chartDataSeries.long_label,
                                shortLabel: chartDataSeries.short_label,
                                val1: xFormatted,
                                val2: yFormatted,
                            });
                        }
                    });
                }
                ychartFund.removeDataTooltip();
                return ychartFund.showDataTooltip(tooltipData);
            };
        };
    }
    setChartHeight(chartDimensions) {
        // Set chart height to maxHeight if height > maxHeight
        if (this.config.dynamicHeight && chartDimensions.height > chartDimensions.maxHeight) {
            this.highchartOptions.chart.height = chartDimensions.maxHeight;
        }
        else {
            this.highchartOptions.chart.height = chartDimensions.height;
        }
    }
    createStockChart(chartId, chartInfo, chartDiv, chartDimensions, chartAnnotations = []) {
        const processedHighchartOptions = this.setProcessedChartOptions(chartInfo, chartDiv, chartDimensions, chartId);
        processedHighchartOptions.annotations = chartAnnotations;
        // renderTo is set on chart options by setProcessedChartOptions, so the single-argument
        // stockChart() factory renders into the right container.
        this.stockChart = Highcharts.stockChart(processedHighchartOptions);
    }
    destroyStockChart() {
        if (this.stockChartExists()) {
            const chartIdx = Highcharts.charts.indexOf(this.stockChart);
            if (chartIdx !== -1) {
                this.stockChart.destroy();
                Highcharts.charts.splice(chartIdx, 1);
            }
        }
    }
    stockChartExists() {
        return !!(this.stockChart &&
            this.stockChart.renderer &&
            Object.keys(this.stockChart.renderer).length !== 0);
    }
    getAnnotationLineLabelY(annotationLineType) {
        return (ANNOTATION_LINE_LABEL_INFO[annotationLineType].label?.y ?? null);
    }
    getChartSizeMultiplier(_chartDimensions = null) {
        /*
        We discard the `chartDimensions` as it is required by the interface but unused in this implementation.
        */
        return 1;
    }
    getLineWidthFromLineWeight(lineWeight, _chartSizeMultiplier) {
        /*
        We discard the `chartSizeMultiplier` as it is required by the interface but unused in this implementation.
        */
        return LINE_WEIGHT_MAPPING[lineWeight];
    }
    formatAnnotationEventLabel(eventItem) {
        const formattedDate = this.dateFormatter.formatDate(eventItem.event_date_js, 'mm/dd/yyyy', true);
        switch (eventItem.event_subgroup) {
            case ANNOTATION_DIVIDENDS: {
                const dividendType = eventItem.extra.dividend_type;
                const formattedAmount = eventItem.extra.amount != null ? getBasicFormatted(eventItem.extra.amount, 'figure', 4) : 'N/A';
                const dividendTypeMarkup = dividendType ? `<span class="info-label">Type</span>: ${dividendType}` : '';
                return `<div class="flag-tooltip">
                            <strong>Dividend: ${formattedDate}</strong>
                            <br>
                            <span class="info-label">Amt</span>: ${formattedAmount} ${dividendTypeMarkup}
                        </div>`;
            }
            case ANNOTATION_EARNINGS_RESULTS: {
                // Check in case fiscal_year is null
                const fiscalQuarterInfo = eventItem.extra.fiscal_year
                    ? `<br>${eventItem.extra.fiscal_quarter} ${eventItem.extra.fiscal_year}`
                    : '';
                return `<div class="flag-tooltip">
                            <strong>Earnings: ${formattedDate}</strong>${fiscalQuarterInfo}
                        </div>`;
            }
            case ANNOTATION_SPLITS:
                return `<div class="flag-tooltip">
                            <strong>Split: ${formattedDate}</strong>
                            <br>$
                            ${eventItem.extra.ratio}
                        </div>`;
            case ANNOTATION_SPINOFFS:
                return `<div class="flag-tooltip">
                            <strong>Spinoff: ${formattedDate}</strong>
                            <br>
                            ${eventItem.extra.ratio}
                        </div>`;
            default:
                return '';
        }
    }
    calculateSubchartOffsets() {
        const chartData = this.chartInfo.chart_data;
        const { chartDimensions } = this;
        const xAxisHeight = this.getXAxisHeight();
        const numCharts = chartData.length;
        const normalChartRatio = 3;
        let numOffsets = 0;
        for (let i = 0; i < numCharts; i += 1) {
            const chart = chartData[i];
            // Normal charts have ratio of 3:1 to correlation charts
            if (chart.length > 1 || chart[0].object_type !== 'correlation') {
                numOffsets += normalChartRatio;
            }
            else {
                numOffsets += 1;
            }
        }
        let curOffset = 0;
        let nextOffset;
        const offset = (chartDimensions.height - xAxisHeight) / numOffsets;
        const subchartOffsets = [];
        // We will want an offset number for the top and bottom of each chart
        chartData.forEach((chart) => {
            // Add the current offset and calculate the next
            subchartOffsets.push(curOffset);
            // If we are on a normal chart, add offsets using the normalChartRatio
            if (chart !== undefined && (chart.length > 1 || chart[0].object_type !== 'correlation')) {
                nextOffset = curOffset + normalChartRatio * offset;
            }
            else {
                nextOffset = curOffset + offset;
            }
            // If we are autoresizing, cap the height at 75% of width
            if (this.config.dynamicHeight) {
                const nextHeight = nextOffset - curOffset;
                if (nextHeight > chartDimensions.maxHeight) {
                    nextOffset = curOffset + chartDimensions.maxHeight;
                }
            }
            curOffset = nextOffset;
        });
        this.highchartOptions.chart.marginBottom = xAxisHeight;
        this.highchartOptions.chart.height = curOffset + xAxisHeight;
        this.highchartOptions.xAxis.tickPixelInterval = chartDimensions.width / this.highchartOptions.xAxis.tickAmount;
        subchartOffsets.push(curOffset);
        return subchartOffsets;
    }
    getTooltipHoverHandler() {
        const that = this;
        // Bound as a point `mouseOver` callback, so `this` is the hovered Highcharts.Point.
        return function () {
            that.createTooltips()(this.x);
        };
    }
    setupHoverEvents() {
        const { tooltipStyle } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BAR) {
            this.highchartOptions.plotOptions.series.point = {
                events: {
                    mouseOver: this.getTooltipHoverHandler(),
                    mouseOut: this.resetDataTooltip(),
                },
            };
        }
        else if (tooltipStyle === TOOLTIP_STYLE_BOX) {
            this.highchartOptions.tooltip.formatter = this.getTooltipBoxHandler();
        }
    }
    /* eslint-disable no-param-reassign, max-len */
    drawChartArrowLabel(chart, chartArrow, yAxisIdx, arrowIdx = 0, styles = {}) {
        const path = [...CHART_ARROW_PATH];
        const idx = chartArrow.labelIdx;
        const highchartYAxisIdx = chartArrow.highchartYAxisIdx;
        const yAxisLabelZIndex = chart.yAxis[highchartYAxisIdx].labelGroup.zIndex;
        // Call renderer for text and measure width to dynamically size the chart chartArrow box
        const renderValue = chartArrow.suffix ? `${chartArrow.yValue} ${chartArrow.suffix}` : chartArrow.yValue;
        // value_and_ticker flags use a <br> so the value wraps to a second line; size the box to fit both.
        const numLines = String(renderValue).split('<br>').length;
        const lineHeight = 14;
        const boxHeight = 16 * numLines;
        if (!chart[`labelTxt${idx}${arrowIdx}`]) {
            chart[`labelTxt${idx}${arrowIdx}`] = chart.renderer.text(renderValue);
        }
        else {
            // Refresh the cached label text on redraw/zoom so it shows the current (last visible) value
            // instead of the value captured when the element was first created.
            chart[`labelTxt${idx}${arrowIdx}`].attr({ text: renderValue });
        }
        if (styles == null)
            styles = {};
        const defaultLabelStyles = {
            'zIndex': yAxisLabelZIndex + 2,
            'fill': 'white',
            'font-size': '12px',
            'font-weight': 'bold',
        };
        const labelStyles = { ...defaultLabelStyles, ...styles };
        // Create new label path
        const labelTxt = chart[`labelTxt${idx}${arrowIdx}`]
            .attr(labelStyles)
            // Raise the first line by half the extra height so the two-line block stays centered.
            .translate(chart.plotSizeX + this.getYAxisWidth() * yAxisIdx + 10, chart.yAxis[highchartYAxisIdx].top +
            chartArrow.plotY +
            5 -
            ((numLines - 1) * lineHeight) / 2)
            .add();
        // Set box drawing coordinates, use svg BBox and fall back to element's clientWidth
        let arrowBoxWidth = (labelTxt.getBBox().width || labelTxt.element.clientWidth) + 12;
        arrowBoxWidth = Math.max(MIN_CHART_ARROW_WIDTH, arrowBoxWidth);
        path[4] = `L${arrowBoxWidth}`;
        path[6] = `L${arrowBoxWidth}`;
        // Grow the box vertically for two-line flags, keeping the arrow tip centered.
        path[1] = `${boxHeight / 2}`;
        path[7] = `${boxHeight}`;
        path[9] = `${boxHeight}`;
        // Call renderer for background
        if (!chart[`labelBg${idx}${arrowIdx}`]) {
            chart[`labelBg${idx}${arrowIdx}`] = chart.renderer.path(path);
        }
        else {
            // Resize the cached background box to match the refreshed label width.
            chart[`labelBg${idx}${arrowIdx}`].attr({ d: path });
        }
        const translateArgs = [
            chart.plotSizeX + this.getYAxisWidth() * yAxisIdx + 1,
            // Shift the taller box up so it stays vertically centered on the arrow tip.
            chart.yAxis[highchartYAxisIdx].top + chartArrow.plotY - 7 - (boxHeight - 16) / 2,
        ];
        chart[`labelBg${idx}${arrowIdx}`]
            .attr({ zIndex: yAxisLabelZIndex + 1, fill: chartArrow.color })
            .translate(...translateArgs)
            .add();
    }
    plotChartArrowLabel(yAxis, chartIdx, sortFunction) {
        const chartHeight = this.subchartOffsets[chartIdx + 1] - this.subchartOffsets[chartIdx];
        // value_and_ticker draws two-line flags, so space them by the full two-line height.
        const arrowHeight = this.config.valueFlags === ValueFlagOption.VALUE_AND_TICKER ? CHART_ARROW_HEIGHT * 2 : CHART_ARROW_HEIGHT;
        let minY = arrowHeight / 2;
        const maxY = chartHeight - arrowHeight / 2;
        yAxis.sort(sortFunction);
        for (let i = 0; i < yAxis.length; i += 1) {
            const chartArrowPoint = yAxis[i];
            const numArrowsLeftToDraw = yAxis.length - 1 - i;
            // Take higher of minY and the arrow yValue
            let y = Math.max(minY, chartArrowPoint.plotY);
            // But if y is greater than maxY, than move it back so it doesnt get cut off
            // Take into account number of arrows left to draw to use as max
            y = Math.min(maxY - numArrowsLeftToDraw * arrowHeight, y);
            chartArrowPoint.plotY = y;
            // Now set the min of the next label to avoid label collision
            minY = y + arrowHeight;
        }
    }
    getTooltipBoxHandler() {
        const ychartFund = this;
        // Bound as a tooltip `formatter`, so `this` is the tooltip formatter context (its `x` is the timestamp).
        return function () {
            return ychartFund.createTooltips()(this.x);
        };
    }
    showDataTooltip(tooltipData) {
        const { tooltipStyle } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BOX) {
            return this.showDataTooltipBox(tooltipData);
        }
        if (tooltipStyle === TOOLTIP_STYLE_BAR) {
            if (this.config.tooltipCallback !== undefined) {
                this.config.tooltipCallback(tooltipData);
            }
        }
        return null;
    }
    showDataTooltipBox(tooltipData) {
        let contents = '<div class="highcharts-tooltip-inner" style="display: block; opacity: 1; z-index: 4;">';
        let lastValue = '';
        const addBullets = tooltipData.length > 2;
        for (let i = 0; i < tooltipData.length; i += 1) {
            for (let j = 0; j < tooltipData[i].length; j += 1) {
                const tooltipDataItem = tooltipData[i][j];
                // Draw header
                if (tooltipDataItem.val1 !== lastValue) {
                    contents += `<div class="highcharts-header">${tooltipDataItem.val1}</div>`;
                    lastValue = tooltipDataItem.val1;
                }
                contents += '<div class="highcharts-tooltip-item">';
                // Show security label and value if point exists from tooltipData
                if (tooltipDataItem.val1 !== '' && tooltipDataItem.val2 !== '') {
                    // If we are showing the colored chart bullets then we need to style it differently than
                    // if we are coloring the values themselves.
                    if (addBullets) {
                        contents += `<div class="highcharts-tooltip-item-name bullet-color">
                                      <span class="chart-bullet-point" style="background-color: ${tooltipDataItem.color};"></span>
                                      ${tooltipDataItem.shortLabel}:
                                      <span class="highcharts-tooltip-item-value" style="color: ${tooltipDataItem.color};">
                                            ${tooltipDataItem.val2}
                                      </span>
                                 </div>
                                 <br></div>`;
                    }
                    else {
                        contents += `<div class="highcharts-tooltip-item-name">
                                      ${tooltipDataItem.shortLabel}:
                                      <span class="highcharts-tooltip-item-value" style="color: ${tooltipDataItem.color};">
                                            ${tooltipDataItem.val2}
                                      </span>
                                 </div>
                                 <br></div>`;
                    }
                }
            }
        }
        contents += '</div></div>';
        return contents;
    }
    resetDataTooltip() {
        const { tooltipStyle } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BOX) {
            this.removeDataTooltipBox();
        }
        else if (tooltipStyle === TOOLTIP_STYLE_BAR) {
            this.resetDataTooltipBar();
        }
    }
    resetDataTooltipBar() {
        const { chartData } = this;
        // First check to see if the data set is not empty,
        // if it is not, reset the bar with the last item of each data series
        let maxLength = 0;
        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                if (chartData[i][j].raw_data.length > maxLength) {
                    maxLength = chartData[i][j].raw_data.length;
                }
            }
        }
        if (maxLength === 0) {
            return;
        }
        if (this.range && this.range.max) {
            const event = {
                target: {
                    x: this.range.max,
                },
            };
            this.createTooltips()(event.target.x);
        }
    }
    removeDataTooltip() {
        const { tooltipStyle } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BOX) {
            this.removeDataTooltipBox();
        }
    }
    removeDataTooltipBox() {
        const tooltipEl = document.getElementById('tooltipDataBox');
        if (tooltipEl)
            tooltipEl.remove();
    }
}
